import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, Users, Package, MapPin, Wallet, Percent,
  Truck, CalendarRange, ArrowUpRight, Download, FileText, UserPlus,
  Radio, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

const KpiCard = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <Card
    data-testid={testId}
    className="p-5 rounded-2xl border-brand-border bg-white hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
  >
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`w-8 h-8 rounded-lg grid place-items-center ${accent || "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="mt-3 font-heading text-3xl font-bold tracking-tight leading-none text-brand-text">{value}</div>
    {sub && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
  </Card>
);

const SCOPES = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "period", label: "Pay Period" },
];

const Dashboard = () => {
  const [scope, setScope] = React.useState("period");
  const [payDate, setPayDate] = React.useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" }));
  const [adjustment, setAdjustment] = React.useState({ driver_id: "", type: "bonus", amount: "", notes: "" });
  const [savingAdjustment, setSavingAdjustment] = React.useState(false);
  const url = scope === "period" ? "/dashboard/kpis" : `/dashboard/range-kpis?scope=${scope}`;
  const { data, error, isLoading, mutate: reloadKpis } = useSWR(url, fetcher);
  const { brand } = useBrand();
  const { user } = useAuth();
  const isDepot = user?.role === "depot_manager";
  const { data: dailyPay, mutate: reloadDailyPay } = useSWR(
    !isDepot && payDate ? `/dashboard/daily-payments?date=${payDate}` : null,
    fetcher,
  );
  const { data: allDrivers = [] } = useSWR(!isDepot ? "/drivers" : null, fetcher);

  const addAdjustment = async () => {
    if (!adjustment.driver_id || !adjustment.amount) return toast.error("Select a driver and enter an amount");
    setSavingAdjustment(true);
    try {
      await api.post("/dashboard/pay-adjustments", {
        ...adjustment, date: payDate, amount: Number(adjustment.amount),
      });
      toast.success("Payment adjustment added. DPD imports will not overwrite it.");
      setAdjustment((v) => ({ ...v, amount: "", notes: "" }));
      await Promise.all([reloadDailyPay(), reloadKpis()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add payment adjustment");
    } finally {
      setSavingAdjustment(false);
    }
  };

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading dashboard…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load KPIs</div>;
  if (!data) return null;

  const showPeriodFinancials = !isDepot && scope === "period";
  const period = data.period;
  const seriesLabelled = (data.daily_series || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
  }));
  const driverRows = (data.by_driver || []).map((d) => ({
    ...d,
    full_name: d.full_name || d.driver_name || "—",
    days: d.days ?? d.work_days ?? 0,
  }));

  const firstName = (user?.full_name || "").split(" ")[0] || "there";
  const nowLondon = new Date().toLocaleString("en-GB", { timeZone: "Europe/London",
    weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* HERO — brand-forward welcome band */}
      <div
        className="relative overflow-hidden rounded-3xl border border-brand-border shadow-sm"
        data-testid="dashboard-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-[#12213b] to-[#1a2f57]" />
        <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-brand-red/25 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full bg-brand-blue/30 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
                      backgroundSize: "22px 22px" }} />

        <div className="relative px-5 py-6 md:px-9 md:py-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          {brand?.logo_url ? (
            <div className="shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/95 shadow-lg p-2.5 grid place-items-center">
              <img src={brand.logo_url} alt={brand.name || "logo"}
                   className="w-full h-full object-contain" />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.35em] text-brand-red font-mono flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              {brand?.product || "MY PORTAL"} · {nowLondon}
            </div>
            <h1 className="mt-1.5 font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">
              Welcome, {firstName}
            </h1>
            <p className="mt-1.5 text-sm md:text-base text-white/70 max-w-xl">
              {brand?.name || "MY CHOICE TRADING LTD"} dashboard — today&apos;s fleet performance, driver activity and operational alerts at a glance.
            </p>
            {scope === "period" && period && (
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/80">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarRange className="w-3.5 h-3.5" />
                  {period.name}: {ddmmyyyy(period.start_date)} — {ddmmyyyy(period.end_date)}
                </span>
                <span className="text-white/30">•</span>
                <span>Payment date {ddmmyyyy(period.payment_date)}</span>
                <Badge variant="outline" className="ml-1 uppercase text-[10px] tracking-wider border-white/40 text-white bg-white/10 backdrop-blur-sm">
                  {period.status}
                </Badge>
              </div>
            )}
          </div>
          <div className="hidden md:flex flex-col gap-2 shrink-0">
            {!isDepot && (
            <Link to="/admin/integrations/dpd" data-testid="hero-cta-dpd"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-brand-navy hover:bg-white/90 text-sm font-semibold shadow-sm transition-colors">
              <Download className="w-4 h-4" /> DPD Import
            </Link>
            )}
            <Link to="/admin/drivers" data-testid="hero-cta-drivers"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 border border-white/25 text-sm font-semibold backdrop-blur transition-colors">
              <UserPlus className="w-4 h-4" /> Add driver
            </Link>
          </div>
        </div>
      </div>

      {/* Scope selector on its own row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">View</div>
          <div className="font-heading text-lg font-bold tracking-tight text-brand-text">
            {scope === "period"
              ? (period ? period.name : "No open pay period")
              : `${data.start_date || ""} – ${data.end_date || ""}`}
          </div>
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="scope-selector">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`scope-${s.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${
                scope === s.key
                  ? "bg-brand-red text-white shadow-sm"
                  : "bg-brand-surface text-brand-text-muted hover:text-brand-text hover:bg-slate-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
        {([
          !isDepot && { to: "/admin/integrations/dpd", label: "DPD Sync", sub: "Import from portal", icon: Radio, color: "bg-brand-red/10 text-brand-red" },
          { to: "/admin/drivers", label: "Drivers", sub: `${data.active_drivers} active`, icon: Users, color: "bg-indigo-50 text-indigo-600" },
          !isDepot && { to: "/admin/pay-periods", label: "Pay periods", sub: "Open or close", icon: CalendarRange, color: "bg-emerald-50 text-emerald-600" },
          !isDepot && { to: "/admin/invoices", label: "Invoices", sub: "Create or download", icon: FileText, color: "bg-amber-50 text-amber-600" },
          isDepot && { to: "/admin/work-planner", label: "Work planner", sub: "Plan drivers and vehicles", icon: CalendarRange, color: "bg-emerald-50 text-emerald-600" },
          isDepot && { to: "/admin/documents", label: "Documents", sub: "Review expiry alerts", icon: FileText, color: "bg-amber-50 text-amber-600" },
        ].filter(Boolean)).map((q) => (
          <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className="group p-4 rounded-2xl border border-brand-border bg-white hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${q.color}`}>
              <q.icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold text-brand-text truncate">{q.label}</div>
              <div className="text-[11px] text-slate-500 truncate">{q.sub}</div>
            </div>
            <ArrowUpRight className="ml-auto w-4 h-4 text-slate-300 group-hover:text-brand-red transition-colors" />
          </Link>
        ))}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {showPeriodFinancials && (
          <>
            <KpiCard
              label="Revenue"
              value={gbp(data.revenue)}
              sub="DPD gross earnings, this period"
              icon={TrendingUp}
              accent="bg-emerald-50 text-emerald-600"
              testId="kpi-revenue"
            />
            <KpiCard
              label="Driver pay"
              value={gbp(data.driver_pay)}
              sub={`Across ${data.by_driver.length} drivers`}
              icon={Wallet}
              accent="bg-slate-100 text-slate-700"
              testId="kpi-driver-pay"
            />
            <KpiCard
              label="Gross profit"
              value={gbp(data.profit)}
              sub="Revenue − driver pay"
              icon={BarChart3}
              accent="bg-brand-red/10 text-brand-red"
              testId="kpi-profit"
            />
            <KpiCard
              label="VAT estimate"
              value={gbp(data.vat_estimate)}
              sub="Flat-rate 10%"
              icon={Percent}
              accent="bg-amber-50 text-amber-600"
              testId="kpi-vat"
            />
          </>
        )}
        <KpiCard label="Active drivers" value={data.active_drivers} icon={Users} accent="bg-indigo-50 text-indigo-600" testId="kpi-active-drivers" />
        <KpiCard label="Work days" value={data.work_days} icon={CalendarRange} accent="bg-slate-100 text-slate-700" testId="kpi-work-days" />
        <KpiCard label="Total stops" value={(data.stops || 0).toLocaleString("en-GB")} icon={MapPin} accent="bg-teal-50 text-teal-600" testId="kpi-stops" />
        <KpiCard label="Total parcels" value={(data.parcels || 0).toLocaleString("en-GB")} icon={Package} accent="bg-fuchsia-50 text-fuchsia-600" testId="kpi-parcels" />
      </div>

      {!isDepot && (
        <Card className="p-5 rounded-2xl border-brand-border bg-white" data-testid="daily-payments-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Daily payments</div>
              <div className="font-heading text-lg font-bold tracking-tight">Pay by driver and source</div>
              <p className="text-xs text-slate-500 mt-1">Bonuses and special payments stay separate from every DPD sync.</p>
            </div>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="w-full md:w-44" />
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="p-3">Driver</th><th className="p-3">Sources</th><th className="p-3 text-right">DPD gross</th><th className="p-3 text-right">Base pay</th><th className="p-3 text-right">Adjustments</th><th className="p-3 text-right">Deductions</th><th className="p-3 text-right">Net day pay</th></tr>
              </thead>
              <tbody>
                {(dailyPay?.drivers || []).map((row) => (
                  <tr key={row.driver_id} className="border-t border-slate-100">
                    <td className="p-3"><div className="font-medium">{row.driver_name}</div><div className="text-[11px] font-mono text-slate-500">{row.driver_code}</div></td>
                    <td className="p-3 text-xs text-slate-600">{row.sources?.join(", ") || "Manual only"}</td>
                    <td className="p-3 text-right">{gbp(row.dpd_gross)}</td>
                    <td className="p-3 text-right">{gbp(row.base_pay)}</td>
                    <td className="p-3 text-right text-emerald-700">{gbp(row.adjustment_total)}</td>
                    <td className="p-3 text-right text-red-700">{gbp(row.deductions_total)}</td>
                    <td className="p-3 text-right font-bold">{gbp(row.day_pay)}</td>
                  </tr>
                ))}
                {!dailyPay?.drivers?.length && <tr><td colSpan="7" className="p-5 text-center text-xs text-slate-500">No payments on this date.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-4">
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={adjustment.driver_id} onChange={(e) => setAdjustment({ ...adjustment, driver_id: e.target.value })}>
              <option value="">Select driver</option>
              {allDrivers.filter((d) => d.status !== "deleted").map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={adjustment.type} onChange={(e) => setAdjustment({ ...adjustment, type: e.target.value })}>
              <option value="bonus">Bonus</option><option value="special_payment">Special payment</option><option value="reimbursement">Reimbursement</option><option value="correction">Correction</option>
            </select>
            <Input type="number" step="0.01" placeholder="Amount (£)" value={adjustment.amount} onChange={(e) => setAdjustment({ ...adjustment, amount: e.target.value })} />
            <Input placeholder="Note (optional)" value={adjustment.notes} onChange={(e) => setAdjustment({ ...adjustment, notes: e.target.value })} />
            <Button className="bg-brand-red hover:bg-brand-red-hover" disabled={savingAdjustment} onClick={addAdjustment}>{savingAdjustment ? "Adding…" : "Add payment"}</Button>
          </div>
        </Card>
      )}

      {/* Chart + By Driver */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 rounded-2xl border-brand-border bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{isDepot ? "Daily stops" : "Daily revenue"}</div>
              <div className="font-heading text-lg font-bold tracking-tight">{isDepot ? "Operational activity by day" : "Route payments by day"}</div>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Live</Badge>
          </div>
          <div className="h-64 min-h-[256px] w-full" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={seriesLabelled} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--brand-red)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--brand-red)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--brand-text-muted)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--brand-text-muted)" }} tickLine={false} axisLine={false} width={50}
                  tickFormatter={(v) => isDepot ? `${v}` : `£${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid var(--brand-border)", fontSize: 12 }}
                  formatter={(v) => isDepot ? [v, "Stops"] : [gbp(v), "Revenue"]}
                />
                <Area type="monotone" dataKey={isDepot ? "stops" : "revenue"} stroke="var(--brand-red)" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-brand-border bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">By driver</div>
              <div className="font-heading text-lg font-bold tracking-tight">This period</div>
            </div>
            <Truck className="w-4 h-4 text-slate-400" />
          </div>
          <div className="space-y-3">
            {driverRows.map((d) => (
              <div key={d.driver_id} data-testid={`by-driver-${d.driver_code}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-brand-text truncate">{d.full_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{d.driver_code} · {d.days}d · {d.stops.toLocaleString("en-GB")} stops</div>
                </div>
                <div className="text-right shrink-0">
                  {!isDepot && (
                    <>
                      <div className="font-heading text-sm font-bold text-brand-text">{gbp(d.net_pay)}</div>
                      <div className={`text-[11px] font-mono inline-flex items-center gap-0.5 ${d.company_gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        <ArrowUpRight className="w-3 h-3" />{gbp(d.company_gross_profit)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            {driverRows.length === 0 && (
              <div className="text-xs text-slate-500">No work rows yet in this period.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
